import { Button, Card, Empty, Input, Select, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

export type EnquiryRow = {
  id: string;
  contact_name: string;
  phone?: string | null;
  email?: string | null;
  grade_interest?: string | null;
  preferred_session_label?: string | null;
  source_channel?: string | null;
  notes?: string | null;
  status?: string | null;
  follow_up_on?: string | null;
  converted_application_id?: string | null;
  created_at?: string | null;
};

const ENQUIRY_STATUSES = ['open', 'contacted', 'converted', 'closed'] as const;

function statusColor(status?: string | null): string | undefined {
  if (status === 'converted') return 'green';
  if (status === 'contacted') return 'blue';
  if (status === 'closed') return undefined;
  return 'gold';
}

function dateOnly(value?: string | null): string {
  return value ? String(value).slice(0, 10) : '—';
}

export default function EnquiryListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canManage = Boolean(useAuthStore((state) => state.user?.permissions?.includes('education.admission.manage')));
  const status = params.get('status') || undefined;
  const q = params.get('q') || undefined;

  const query = useQuery({
    queryKey: ['education-enquiries', status, q],
    queryFn: () => api.get<EnquiryRow[]>('/education/admissions/enquiries', { params: { status, q } }),
  });
  const rows = Array.isArray(query.data?.data) ? query.data!.data : [];

  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <EducationPageChrome
      title={t('enquiriesTitle', { defaultValue: 'Admission enquiries' })}
      description={t('enquiriesIntro', {
        defaultValue: 'Track prospective-parent enquiries, follow-ups, and convert an enquiry into a draft application.',
      })}
      tags={<Tag>{rows.length} {t('enquiriesTitle', { defaultValue: 'Admission enquiries' })}</Tag>}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/admissions')}>{t('backToAdmissions')}</Button>
          {canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/education/admissions/enquiries/new')}>
              {t('newEnquiry', { defaultValue: 'New enquiry' })}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Card size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t('search')}
              defaultValue={q}
              onSearch={(value) => setFilter('q', value || undefined)}
              style={{ width: 240 }}
            />
            <Select
              allowClear
              placeholder={t('status')}
              value={status}
              style={{ minWidth: 160 }}
              options={ENQUIRY_STATUSES.map((item) => ({ value: item, label: item }))}
              onChange={(value) => setFilter('status', value)}
            />
          </Space>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={rows}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('enquiriesEmpty', { defaultValue: 'No enquiries yet' })} /> }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/admissions/enquiries/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('name'), dataIndex: 'contact_name' },
              { title: t('phone'), dataIndex: 'phone', width: 140, render: (v: string | null) => v || '—' },
              { title: t('email'), dataIndex: 'email', ellipsis: true, render: (v: string | null) => v || '—' },
              { title: t('grade'), dataIndex: 'grade_interest', width: 120, render: (v: string | null) => v || '—' },
              { title: t('source', { defaultValue: 'Source' }), dataIndex: 'source_channel', width: 120, render: (v: string | null) => v || '—' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v?: string | null) => <Tag color={statusColor(v)}>{v || 'open'}</Tag>,
              },
              {
                title: t('followUpOn', { defaultValue: 'Follow-up' }),
                dataIndex: 'follow_up_on',
                width: 120,
                render: (v?: string | null) => dateOnly(v),
              },
            ]}
          />
        </Space>
      </Card>
    </EducationPageChrome>
  );
}
